import React, { useState } from 'react';
import { User, Lock, Mail, Loader2, CheckCircle, ArrowLeft } from 'lucide-react';
import { register } from './api';
import { useLanguage } from './LanguageContext';

export default function RegisterScreen({ onBack, onRegisterSuccess }) {
    const { t } = useLanguage();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!username || !email || !password || !confirmPassword) {
            setError(t('register.errorMissing'));
            return;
        }

        if (password !== confirmPassword) {
            setError(t('register.errorMismatch'));
            return;
        }

        if (password.length < 8) {
            setError(t('register.errorLength'));
            return;
        }

        setLoading(true);
        try {
            await register({
                username,
                email,
                password,
                password_confirmation: confirmPassword,
            });
            setSuccess(true);
            setTimeout(() => {
                onRegisterSuccess();
            }, 2000);
        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                // Format validation errors
                const errors = error.response.data.errors;
                const firstError = Object.values(errors)[0][0];
                setError(firstError);
            } else {
                setError(t('register.errorFailed'));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-indigo-600 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
                <div className="p-8">
                    <div className="text-center mb-8 relative">
                        <button onClick={onBack} className="absolute left-0 top-0 text-gray-400 hover:text-gray-600">
                            <ArrowLeft size={24} />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800">{t('register.title')}</h1>
                        <p className="text-gray-500 mt-2">{t('register.subtitle')}</p>
                    </div>

                    {success ? (
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <CheckCircle className="h-5 w-5 text-green-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-green-700">
                                        {t('register.success')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('register.usernameLabel')}</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <User size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                                        placeholder={t('register.usernamePlaceholder')}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('register.emailLabel')}</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Mail size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                                        placeholder={t('register.emailPlaceholder')}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('register.passwordLabel')}</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                                        placeholder={t('register.passwordPlaceholder')}
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('register.confirmPasswordLabel')}</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Lock size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="pl-10 block w-full rounded-lg border-gray-300 bg-gray-50 border focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2.5"
                                        placeholder={t('register.confirmPasswordPlaceholder')}
                                        required
                                    />
                                </div>
                            </div>

                            {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors mt-6 ${loading ? 'opacity-75 cursor-not-allowed' : ''}`}
                            >
                                {loading ? (
                                    <div className="flex items-center">
                                        <Loader2 className="animate-spin mr-2" size={18} />
                                        {t('register.submitting')}
                                    </div>
                                ) : (
                                    t('register.submitButton')
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
